import { dayCodec, toDayParam, type UrlCodec } from "@/lib/urlFilters.util";

export type DateFilterType = "DAY" | "WEEK" | "MONTH" | "RANGE";

export type DateFilterField = "pickupDate" | "finalizedAt";

export function getWeekStart(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  return d;
}

export function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

export function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function endOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

export const dayRange = (day: Date): [Date, Date] => [startOfDay(day), endOfDay(day)];

// pickup only happens on the work week (Monday to Friday), the rest can span the full week
export const WORK_WEEK_LENGTH = 5;
export const FULL_WEEK_LENGTH = 7;

export const weekLengthForField = (field: DateFilterField): number =>
  field === "finalizedAt" ? FULL_WEEK_LENGTH : WORK_WEEK_LENGTH;

export const weekRange = (weekStart: Date, weekLength: number = WORK_WEEK_LENGTH): [Date, Date] => [
  startOfDay(weekStart),
  endOfDay(addDays(weekStart, weekLength - 1)),
];

export const monthRange = (month: Date): [Date, Date] => [
  new Date(month.getFullYear(), month.getMonth(), 1, 0, 0, 0, 0),
  new Date(month.getFullYear(), month.getMonth() + 1, 0, 23, 59, 59, 999),
];

export type DateFilterPatch<T extends DateFilterType = DateFilterType> =
  | { dateAt: [Date, Date]; dateFilterType: T }
  | { dateAt: null; dateFilterType: null };

export const CLEARED_DATE_FILTER = { dateAt: null, dateFilterType: null } as const;

export const dayFilter = (day: Date | null): DateFilterPatch<"DAY"> =>
  day ? { dateAt: dayRange(day), dateFilterType: "DAY" } : CLEARED_DATE_FILTER;

export const weekFilter = (
  active: boolean,
  weekStart: Date,
  weekLength: number = WORK_WEEK_LENGTH,
): DateFilterPatch<"WEEK"> =>
  active
    ? { dateAt: weekRange(weekStart, weekLength), dateFilterType: "WEEK" }
    : CLEARED_DATE_FILTER;

export const monthFilter = (active: boolean, month: Date): DateFilterPatch<"MONTH"> =>
  active ? { dateAt: monthRange(month), dateFilterType: "MONTH" } : CLEARED_DATE_FILTER;

export const rangeFilter = (range: [Date, Date] | null): DateFilterPatch<"RANGE"> =>
  range
    ? { dateAt: [startOfDay(range[0]), endOfDay(range[1])], dateFilterType: "RANGE" }
    : CLEARED_DATE_FILTER;

export const weekNavigationFilter = (
  currentType: DateFilterType | null,
  weekStart: Date,
  weekLength: number = WORK_WEEK_LENGTH,
): DateFilterPatch<"WEEK"> | null => {
  if (currentType === "WEEK") return weekFilter(true, weekStart, weekLength);
  if (currentType === "DAY") return CLEARED_DATE_FILTER;
  return null;
};

export const resolveDateFilterType = <T extends DateFilterType>(
  dateAt: [Date, Date] | null,
  dateFilterType: T | null,
): T | "RANGE" | null => (dateAt ? dateFilterType ?? "RANGE" : null);

export const weekStartCodec = (param = "weekStart"): UrlCodec<Date> =>
  dayCodec(param, {
    normalize: getWeekStart,
    isDefault: (date) => toDayParam(date) === toDayParam(getWeekStart(new Date())),
  });

export function dateRangeToParams(
  field: DateFilterField,
  range: [Date, Date] | null,
): {
  pickupDateAfter?: string;
  pickupDateBefore?: string;
  finalizedAfter?: string;
  finalizedBefore?: string;
} {
  if (!range) return {};
  const [from, to] = [range[0].toISOString(), range[1].toISOString()];
  return field === "finalizedAt"
    ? { finalizedAfter: from, finalizedBefore: to }
    : { pickupDateAfter: from, pickupDateBefore: to };
}
